/**
 * Мастер разбора порции — 5 шагов (AK-546, ч.2; wireframe состояния 4-9).
 *
 * Что пришло → Уже есть в справочнике → Платформа предложила → Нужен человек →
 * Готово. Ключевые правила, из-за которых мастер выглядит именно так:
 *
 *  · **прогресс считается от требующих разбора**, а не от всего пришедшего:
 *    из 135 названий 100 уже заведены, и 135 строк работы — враньё;
 *  · **предложение платформы решением не является** (AK-545): «Согласен» —
 *    отдельное действие человека, до него в справочник ничего не пишется;
 *  · шаг «уже есть» — ОТЧЁТ сверки, единственное действие «Это не она»
 *    (решение З): строка едет в ручной разбор, ничего не удаляя;
 *  · **ощущение конца не подделываем**: ушли с неразобранными — экран пишет
 *    «Осталось разобрать: N из M · разнесено K» и предлагает продолжить позже.
 */
import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleAlert,
  Loader2,
  MoveRight,
  Undo2,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TileGrid } from '@/components/ui/tile-grid';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { formatRuDate } from '@/lib/dateFmt';
import { ApiError } from '@/lib/api';
import {
  useAgreeAll,
  useAgreeItem,
  useCompleteBatch,
  useDeferBatch,
  useDisputeItem,
  useReviewBatch,
  useReviewItems,
  useStartBatch,
  useUndoItem,
  type BatchCounts,
  type ReviewEntityType,
  type ReviewItem,
} from '@/lib/reference/catalogReview';
import { OccupancyDialog } from './OccupancyDialog';
import {
  WIZARD_STEPS,
  canOpenStep,
  doneHeadline,
  plural,
  progressLabel,
  progressPct,
  shownOfLabel,
  originDate,
  originLabel,
  stepHint,
  weightLabel,
  yearsLabel,
  type WizardStep,
} from './reviewLabels';

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

// ─── шапка: степпер + прогресс ───────────────────────────────────────────────

function Stepper({
  step,
  counts,
  finished,
  onStep,
}: {
  step: WizardStep;
  counts: BatchCounts;
  /** null = «Завершить разбор» ещё не нажимали. */
  finished: { fully: boolean; created: number } | null;
  onStep: (s: WizardStep) => void;
}) {
  const idx = WIZARD_STEPS.findIndex((s) => s.key === step);
  return (
    <div className="grid grid-cols-1 overflow-hidden rounded-lg border border-border sm:grid-cols-5">
      {WIZARD_STEPS.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        // Шаг «Готово» — результат завершения, а не вкладка: пускать на него
        // кликом у незавершённой порции значит рисовать фальшивый конец.
        const openable = canOpenStep(s.key, finished);
        return (
          <button
            key={s.key}
            type="button"
            disabled={!openable}
            title={openable ? undefined : 'Шаг откроется после «Завершить разбор»'}
            onClick={() => openable && onStep(s.key)}
            aria-current={active ? 'step' : undefined}
            aria-disabled={!openable}
            className={cn(
              'flex items-center gap-2 border-b px-3 py-3 text-left transition-colors sm:border-b-0 sm:border-r',
              'border-border last:border-0',
              active && 'bg-accent',
              done && 'bg-primary/10',
              !active && !done && 'opacity-60',
              !openable && 'cursor-not-allowed',
            )}
          >
            <span
              className={cn(
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-xs font-bold',
                done && 'border-primary bg-primary text-primary-foreground',
                active && 'border-accent-brand bg-accent-brand text-accent-brand-foreground',
                !active && !done && 'border-border text-muted-foreground',
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-foreground">
                {s.title}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {stepHint(s.key, counts)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── шаг 1: что пришло ───────────────────────────────────────────────────────

function CountsTiles({ counts }: { counts: BatchCounts }) {
  const tiles = [
    { v: counts.total, label: 'всего пришло названий', tone: 'text-foreground' },
    { v: counts.existing, label: 'уже есть в справочнике — разбирать нечего', tone: 'text-primary' },
    { v: counts.suggested, label: 'новые, платформа предложила', tone: 'text-foreground' },
    { v: counts.manual, label: 'нужен человек', tone: 'text-accent-brand' },
  ];
  return (
    <TileGrid cols={4}>
      {tiles.map((t) => (
        <Card key={t.label} className="p-4">
          <div className={cn('text-2xl font-bold', t.tone)}>{t.v}</div>
          <div className="mt-1 text-xs text-muted-foreground">{t.label}</div>
        </Card>
      ))}
    </TileGrid>
  );
}

// ─── общие куски строк ───────────────────────────────────────────────────────

function RawCell({ item, entityType }: { item: ReviewItem; entityType: ReviewEntityType }) {
  const years = yearsLabel(item.first_year, item.last_year);
  return (
    <div className="flex flex-col gap-1">
      <span className="font-medium text-foreground">{item.raw_name}</span>
      <span className="text-xs text-muted-foreground">
        {weightLabel(item.occurrence_count, entityType)}
        {years && ` · ${years}`}
      </span>
    </div>
  );
}

function EmptyRow({ text }: { text: string }) {
  return (
    <Card className="p-8 text-center text-sm text-muted-foreground">{text}</Card>
  );
}

function ItemsSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full rounded-md" />
      ))}
    </div>
  );
}


/** Подвал списка: усечение выдачи видно, и его можно снять. */
function ItemsFooter({
  shown,
  total,
  hasMore,
  loading,
  onMore,
}: {
  shown: number;
  total: number;
  hasMore: boolean;
  loading: boolean;
  onMore: () => void;
}) {
  if (total <= shown && !hasMore) return null;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm text-muted-foreground">{shownOfLabel(shown, total)}</span>
      {hasMore && (
        <Button variant="outline" size="sm" disabled={loading} onClick={onMore}>
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Показать ещё
        </Button>
      )}
    </div>
  );
}

// ─── мастер ──────────────────────────────────────────────────────────────────

export function BatchWizard({
  entityType,
  batchId,
  canDecide,
  onBack,
}: {
  entityType: ReviewEntityType;
  batchId: string;
  /** Право «разбирать порции» (решение Е). Без него — только чтение. */
  canDecide: boolean;
  onBack: () => void;
}) {
  const [step, setStep] = useState<WizardStep>('incoming');
  const [replaceItem, setReplaceItem] = useState<ReviewItem | null>(null);
  // null = «Завершить разбор» не нажимали. Шаг «Готово» без этого не откроется.
  const [finished, setFinished] = useState<{ fully: boolean; created: number } | null>(null);

  const batchQuery = useReviewBatch(entityType, batchId);
  const batch = batchQuery.data;
  const counts: BatchCounts = batch?.counts ?? {
    total: 0,
    existing: 0,
    suggested: 0,
    manual: 0,
    needs_review: 0,
    decided: 0,
    pending: 0,
    pending_suggested: 0,
    pending_manual: 0,
  };

  const bucket = step === 'existing' ? 'existing' : step === 'suggested' ? 'suggested' : step === 'manual' ? 'manual' : null;
  const itemsQuery = useReviewItems(entityType, batchId, bucket);
  const items = useMemo(
    () => (itemsQuery.data?.pages ?? []).flat(),
    [itemsQuery.data],
  );
  /** Сколько строк в этом бакете ВСЕГО (по серверным счётчикам, не по странице). */
  const bucketTotal =
    step === 'existing'
      ? counts.existing
      : step === 'suggested'
        ? counts.suggested
        : step === 'manual'
          ? counts.manual
          : counts.total;

  const agree = useAgreeItem(entityType, batchId);
  const agreeAll = useAgreeAll(entityType, batchId);
  const dispute = useDisputeItem(entityType, batchId);
  const undo = useUndoItem(entityType, batchId);
  const start = useStartBatch(entityType, batchId);
  const defer = useDeferBatch(entityType, batchId);
  const complete = useCompleteBatch(entityType, batchId);


  function handleStart() {
    // Сверяем порцию, если её ещё ни разу не сверяли: ретро-порцию миграция
    // залила без прогона резолвера, и без этого шага в «нужен человек» лежало
    // бы всё непривязанное — работа, которой на самом деле нет.
    start.mutate(undefined, {
      onSuccess: () => setStep('existing'),
      onError: (e) => toast.error(errMsg(e, 'Не удалось начать разбор')),
    });
  }

  function handleComplete() {
    complete.mutate(undefined, {
      onSuccess: (r) => {
        setFinished({ fully: r.fully_resolved, created: r.created_items });
        setStep('done');
      },
      onError: (e) => toast.error(errMsg(e, 'Не удалось завершить разбор')),
    });
  }

  if (batchQuery.isLoading) {
    return <Skeleton className="h-64 w-full rounded-lg" />;
  }
  if (batchQuery.isError || !batch) {
    return (
      <Alert variant="destructive">
        <CircleAlert className="h-4 w-4" />
        <AlertTitle>Не удалось открыть</AlertTitle>
        <AlertDescription>
          <Button variant="outline" size="sm" onClick={() => batchQuery.refetch()}>
            Повторить
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          К списку
        </Button>
        <Badge variant="outline">{originLabel(batch)}</Badge>
        <span className="text-sm text-muted-foreground">
          {formatRuDate(originDate(batch))}
        </span>
        {batch.source_label && (
          <span className="text-sm text-muted-foreground">{batch.source_label}</span>
        )}
      </div>

      <Stepper step={step} counts={counts} finished={finished} onStep={setStep} />

      {/* Прогресс — от требующих разбора */}
      {step !== 'incoming' && counts.needs_review > 0 && (
        <div className="flex flex-col gap-1">
          <div
            className="h-2 overflow-hidden rounded-full bg-muted"
            role="progressbar"
            aria-valuenow={progressPct(counts)}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="h-full bg-primary" style={{ width: `${progressPct(counts)}%` }} />
          </div>
          <p className="text-xs text-muted-foreground">{progressLabel(counts)}</p>
        </div>
      )}

      {!canDecide && (
        <Alert>
          <AlertDescription>
            У вас нет права разбирать — экран доступен только для просмотра.
            Право выдаёт администратор холдинга.
          </AlertDescription>
        </Alert>
      )}

      {/* ── шаг 1 ── */}
      {step === 'incoming' && (
        <div className="flex flex-col gap-4">
          <CountsTiles counts={counts} />
          {counts.needs_review > 0 ? (
            <Alert>
              <AlertTitle>
                Разнести надо {counts.needs_review} из {counts.total}
              </AlertTitle>
              <AlertDescription>
                Остальные {counts.existing} совпали с тем, что уже заведено — прогресс
                считается от {counts.needs_review}, чтобы объём работы не пугал зря.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert>
              <AlertTitle>Разбирать нечего</AlertTitle>
              <AlertDescription>
                Все эти названия уже есть в справочнике.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              variant="ghost"
              disabled={!canDecide || defer.isPending}
              onClick={() =>
                defer.mutate(undefined, {
                  onSuccess: () => {
                    toast.success('Отложено — ждёт во вкладке интеграций');
                    onBack();
                  },
                  onError: (e) => toast.error(errMsg(e, 'Не удалось отложить')),
                })
              }
            >
              Отложить
            </Button>
            <Button
              disabled={!canDecide || start.isPending}
              onClick={handleStart}
            >
              {start.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {start.isPending && batch.reconciled_at === null
                ? 'Сверяем со справочником…'
                : 'Начать разбор'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── шаг 2: уже есть в справочнике ── */}
      {step === 'existing' && (
        <div className="flex flex-col gap-4">
          <Alert>
            <AlertTitle>{counts.existing} названий уже есть в вашем справочнике</AlertTitle>
            <AlertDescription>
              Совпадение точное — после приведения к одному виду (ё=е, регистр, лишние
              пробелы). Создавать ничего не нужно. Если сверка склеила разное — нажмите
              «Это не она», строка уйдёт в ручной разбор.
            </AlertDescription>
          </Alert>
          {itemsQuery.isLoading ? (
            <ItemsSkeleton />
          ) : items.length === 0 ? (
            <EmptyRow text="Совпадений со справочником нет — всё пришедшее новое." />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[46%]">Пришло из источника</TableHead>
                    <TableHead className="w-[30%]">Есть в справочнике</TableHead>
                    <TableHead className="w-[24%] text-right">&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <RawCell item={item} entityType={entityType} />
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                          <MoveRight className="h-3.5 w-3.5" />
                          то же название
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!canDecide || dispute.isPending}
                          onClick={() =>
                            dispute.mutate(
                              { itemId: item.id },
                              {
                                onSuccess: () =>
                                  toast.success('Строка ушла в ручной разбор'),
                                onError: (e) =>
                                  toast.error(errMsg(e, 'Не удалось оспорить')),
                              },
                            )
                          }
                        >
                          Это не она
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <ItemsFooter
            shown={items.length}
            total={bucketTotal}
            hasMore={Boolean(itemsQuery.hasNextPage)}
            loading={itemsQuery.isFetchingNextPage}
            onMore={() => void itemsQuery.fetchNextPage()}
          />
          <div className="flex justify-end">
            <Button onClick={() => setStep('suggested')}>
              Дальше: предложения ({counts.suggested})
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── шаг 3: платформа предложила ── */}
      {step === 'suggested' && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Этих названий в справочнике ещё нет. Слева — как называется у вас,
              справа — чем платформа это посчитала.
            </p>
            {counts.pending_suggested > 0 && (
              <Button
                variant="outline"
                size="sm"
                disabled={!canDecide || agreeAll.isPending}
                onClick={() =>
                  agreeAll.mutate(undefined, {
                    onSuccess: (r) =>
                      toast.success(`Согласовано ${r.agreed}`),
                    onError: (e) => toast.error(errMsg(e, 'Не удалось согласовать')),
                  })
                }
              >
                {agreeAll.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {/* Число — с сервера: длина загруженной страницы соврала бы. */}
                Согласиться со всеми оставшимися ({counts.pending_suggested})
              </Button>
            )}
          </div>
          {itemsQuery.isLoading ? (
            <ItemsSkeleton />
          ) : items.length === 0 ? (
            <EmptyRow text="Платформа ничего не предложила по этим названиям." />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%]">Пришло</TableHead>
                    <TableHead className="w-[24%]">Платформа считает, что это</TableHead>
                    <TableHead className="w-[36%] text-right">Решение</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const decided = item.decision !== 'pending';
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <RawCell item={item} entityType={entityType} />
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {item.suggested_kind === 'field_state'
                              ? 'пар / АМП'
                              : item.suggested_kind === 'land_category'
                                ? 'угодье'
                                : item.suggested_kind === 'operation_type'
                                  ? 'тех.операция'
                                  : 'культура'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-2">
                            {decided ? (
                              <>
                                <Badge variant="primary">Согласовано</Badge>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={!canDecide || undo.isPending}
                                  onClick={() =>
                                    undo.mutate(item.id, {
                                      onError: (e) =>
                                        toast.error(errMsg(e, 'Не удалось отменить')),
                                    })
                                  }
                                >
                                  <Undo2 className="mr-1 h-3.5 w-3.5" />
                                  Отменить
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button
                                  size="sm"
                                  disabled={!canDecide || agree.isPending}
                                  onClick={() =>
                                    agree.mutate(item.id, {
                                      onError: (e) =>
                                        toast.error(errMsg(e, 'Не удалось согласиться')),
                                    })
                                  }
                                >
                                  <Check className="mr-1 h-3.5 w-3.5" />
                                  Согласен
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={!canDecide}
                                  onClick={() => setReplaceItem(item)}
                                >
                                  Заменить
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
          <ItemsFooter
            shown={items.length}
            total={bucketTotal}
            hasMore={Boolean(itemsQuery.hasNextPage)}
            loading={itemsQuery.isFetchingNextPage}
            onMore={() => void itemsQuery.fetchNextPage()}
          />
          <div className="flex justify-end">
            <Button onClick={() => setStep('manual')}>
              Дальше: нужен человек ({counts.manual})
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── шаг 4: нужен человек ── */}
      {step === 'manual' && (
        <div className="flex flex-col gap-4">
          <Alert>
            <CircleAlert className="h-4 w-4" />
            <AlertTitle>Здесь может быть вообще не культура</AlertTitle>
            <AlertDescription>
              Платформа не поняла эти строки именно потому, что они часто не про
              культуры: смесь, пар или АМП, угодье, а то и служебная строка чужой
              системы. Смотрите на каждую отдельно.
            </AlertDescription>
          </Alert>
          {itemsQuery.isLoading ? (
            <ItemsSkeleton />
          ) : items.length === 0 ? (
            <EmptyRow text="Ручного разбора не осталось." />
          ) : (
            <div className="overflow-hidden rounded-lg border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[52%]">Пришло из источника</TableHead>
                    <TableHead className="w-[20%]">Состояние</TableHead>
                    <TableHead className="w-[28%] text-right">&nbsp;</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <RawCell item={item} entityType={entityType} />
                        {item.disputed_at && (
                          <Badge variant="accent-brand" className="mt-1">
                            оспорено на сверке
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.decision === 'pending' ? (
                          <Badge variant="outline">не разобрано</Badge>
                        ) : item.decision === 'dismissed' ? (
                          <Badge variant="secondary">не нужно в справочнике</Badge>
                        ) : (
                          <Badge variant="primary">разобрано</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {item.decision === 'pending' ? (
                          <Button
                            size="sm"
                            disabled={!canDecide}
                            onClick={() => setReplaceItem(item)}
                          >
                            Разобрать
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={!canDecide || undo.isPending}
                            onClick={() =>
                              undo.mutate(item.id, {
                                onError: (e) =>
                                  toast.error(errMsg(e, 'Не удалось отменить')),
                              })
                            }
                          >
                            <Undo2 className="mr-1 h-3.5 w-3.5" />
                            Отменить
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <ItemsFooter
            shown={items.length}
            total={bucketTotal}
            hasMore={Boolean(itemsQuery.hasNextPage)}
            loading={itemsQuery.isFetchingNextPage}
            onMore={() => void itemsQuery.fetchNextPage()}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              Можно завершить, оставив непонятные на потом — они останутся в
              интеграциях.
            </p>
            <Button
              disabled={!canDecide || complete.isPending}
              onClick={handleComplete}
            >
              {complete.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Завершить разбор
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ── шаг 5: готово ── */}
      {step === 'done' && (
        <Card
          className={cn(
            'flex flex-col items-center gap-3 p-8 text-center',
            finished?.fully ? 'border-primary/40 bg-primary/10' : 'border-border',
          )}
        >
          <p className="text-lg font-bold text-foreground">{doneHeadline(counts, finished)}</p>
          <p className="text-sm text-muted-foreground">
            {originLabel(batch)}
            {batch.source_label ? ` · ${batch.source_label}` : ''} · пришло{' '}
            {counts.total} названий, {counts.existing} уже были в справочнике
          </p>
          <div className="w-full">
            <CountsTiles counts={counts} />
          </div>
          {finished !== null && finished.created > 0 && (
            <p className="text-sm text-foreground">
              Добавлено в справочник: {finished.created}{' '}
              {plural(finished.created, 'запись', 'записи', 'записей')}
            </p>
          )}
          <div className="flex flex-wrap justify-center gap-2">
            {finished?.fully === false && (
              <Button variant="outline" onClick={() => setStep('manual')}>
                Продолжить позже
              </Button>
            )}
            <Button onClick={onBack}>Вернуться к списку</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Разбор не меняет отметку «хватает ли культур для работы» — её ставит
            человек, а не счётчик разобранных строк.
          </p>
        </Card>
      )}

      <OccupancyDialog
        item={replaceItem}
        entityType={entityType}
        batchId={batchId}
        open={replaceItem !== null}
        onOpenChange={(o) => !o && setReplaceItem(null)}
      />
    </div>
  );
}

export default BatchWizard;
